/* eslint-disable no-param-reassign */
import _ from 'underscore';

import { generateGuid, cloneDeep } from '@bingads-webui-universal/primitive-utilities';

import {
  getDataSourceByKeys,
  getComponentByType,
} from './site-config-reader';
import {
  DATA_SOURCE_KEY_MAPPING,
  COMPONENT_TYPE_MAPPING,
  WEBSITE_SOURCE_DATA_KEY_MAPPING,
  DATA_SOURCE_TO_WEBSITE_SOURCE_DATA_MAPPING,
} from './site-config-structure';
import { mapHoursToWSDFormat } from './time-util';
import { linkNameToModel } from './social-link-util';
import { getImageUrlById } from './image/site-config-image-reader';
import { getComponentsPilotMapping } from './component-utils';

export const convertCallToActionId = (callToActions) => {
  _.each(callToActions, (action) => {
    action.CallToActionId = action.CallToActionId || action.Id;
    delete action.Id;
  });
};

export const updateWebsiteSourceDataWithBasicData = (siteConfig, smartPageData, key, overwrite) => {
  const wsdKey = DATA_SOURCE_TO_WEBSITE_SOURCE_DATA_MAPPING[key];

  if (!wsdKey || _.isEmpty(siteConfig) || _.isEmpty(smartPageData)) {
    return;
  }

  siteConfig.websiteSourceData = siteConfig.websiteSourceData || {};

  const shouldSetValue = overwrite ||
    (!overwrite && _.isEmpty(siteConfig.websiteSourceData[wsdKey]));

  if (shouldSetValue) {
    if (wsdKey === WEBSITE_SOURCE_DATA_KEY_MAPPING.Email && !_.isEmpty(smartPageData[key])) {
      [siteConfig.websiteSourceData[wsdKey]] = smartPageData[key];

      return;
    }

    siteConfig.websiteSourceData[wsdKey] = smartPageData[key];

    if (wsdKey === WEBSITE_SOURCE_DATA_KEY_MAPPING.CallToActions) {
      siteConfig.websiteSourceData.CallToActions =
        cloneDeep(siteConfig.websiteSourceData.CallToActions);
      convertCallToActionId(siteConfig.websiteSourceData.CallToActions);
    }
  }
};

export const createWebsiteSourceData = (siteConfig, serverContext = {}) => {
  const componentsPilotMapping = getComponentsPilotMapping(serverContext);

  const [
    businessName,
    businessDescription,
    businessCategories,
    callToActions,
    locationInfo,
    phoneNumber,
    logoImageId,
    emails,
  ] = getDataSourceByKeys(siteConfig, [
    DATA_SOURCE_KEY_MAPPING.BusinessName,
    DATA_SOURCE_KEY_MAPPING.BusinessDescription,
    DATA_SOURCE_KEY_MAPPING.BusinessCategories,
    DATA_SOURCE_KEY_MAPPING.CallToActions,
    DATA_SOURCE_KEY_MAPPING.LocationInfo,
    DATA_SOURCE_KEY_MAPPING.PhoneNumber,
    DATA_SOURCE_KEY_MAPPING.LogoImageId,
    DATA_SOURCE_KEY_MAPPING.Emails,
    DATA_SOURCE_KEY_MAPPING.Theme,
  ]);
  const subDomain = siteConfig.subDomain || generateGuid();
  const descriptionFromCategory = businessCategories && businessCategories.join(', ');
  const description = businessDescription || descriptionFromCategory || '';

  const coverImageInfo = getComponentByType(siteConfig, DATA_SOURCE_KEY_MAPPING.CoverImage);
  const coverImageUrl = (coverImageInfo && coverImageInfo.imageId)
    ? getImageUrlById(siteConfig, coverImageInfo.imageId)
    : siteConfig.localImageUrl;

  const galleryImageInfo = getComponentByType(siteConfig, COMPONENT_TYPE_MAPPING.Gallery);
  const galleryPhotos = galleryImageInfo ? galleryImageInfo.photos : [];
  const galleryImageIds = _.map(galleryPhotos, photo => photo.imageId);
  const galleryImagesUrls = _.chain(galleryImageIds)
    .map(imageId => getImageUrlById(siteConfig, imageId))
    .filter(item => !!item)
    .value();

  const socialLinksInfo = getComponentByType(siteConfig, COMPONENT_TYPE_MAPPING.SocialLinks);
  const socialLinks = _.keys(socialLinksInfo).reduce((acc, key) => {
    if (linkNameToModel[key] && socialLinksInfo[key].isDisplay) {
      const linkName = linkNameToModel[key];
      acc[linkName] = socialLinksInfo[key].value;
    }
    return acc;
  }, {});

  // When business hours componment is available, read data from it,
  // otherwise read data from Contact.
  const businessHoursSourceInfo = getComponentByType(
    siteConfig,
    componentsPilotMapping[COMPONENT_TYPE_MAPPING.BusinessHours]
      ? COMPONENT_TYPE_MAPPING.BusinessHours
      : COMPONENT_TYPE_MAPPING.Contact
  );
  const businessHoursInfo = businessHoursSourceInfo ? businessHoursSourceInfo.businessHours : [];
  const businessHours = mapHoursToWSDFormat(businessHoursInfo);

  const newCallToActions = cloneDeep(callToActions) || [];
  // OData model uses Id, so we have to convert Id to CallToActionId.
  convertCallToActionId(newCallToActions);

  // The following fields are already in WSD, retrieve them directly
  const websiteSourceData = siteConfig.websiteSourceData || {};
  const {
    EmbeddedVideos,
    Locations,
    Theme,
    Services,
    Appointment,
  } = websiteSourceData;

  return {
    BusinessName: businessName,
    Description: description,
    Categories: businessCategories,
    SingleLineAddress: locationInfo,
    PhoneNumber: phoneNumber,
    CoverImageUrl: coverImageUrl,
    CoverImageId: coverImageInfo && coverImageInfo.imageId,
    LogoImageId: logoImageId,
    LogoImageUrl: getImageUrlById(siteConfig, logoImageId),
    GalleryImages: galleryImagesUrls,
    GalleryImageIds: galleryImageIds,
    Subdomain: subDomain,
    Email: emails && emails[0],
    SocialUrls: socialLinks,
    BusinessHours: businessHours,
    CallToActions: newCallToActions,
    EmbeddedVideos: EmbeddedVideos || [],
    Locations: Locations || [],
    Theme: Theme || {
      Mode: 'Light',
      AccentColor: 'Default',
    },
    Services: Services || [],
    Appointment: Appointment || {},
  };
};
